// GroupedProjectFinancials.jsx
import { useState } from "react";
import { Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { formatNumber } from "../../../../../../utils/utils";
import { ProjectFinancialsStyled } from "../project-financials.styles";
import { useLinkedProjects } from "../../../../../../hooks/useLinkedProjects";
import ProgressBar from "../../components/progress-bar/progress-bar";
import Accordian from "../../components/accordian/accordian";
import DeliverableProgressWithLabel from "../../components/deliverable-progress-with-label/deliverable-progress-with-label";
import Loader from "../../../../../../components/loader/loader";

import {
  calculateBudgetForDeliverable,
  getRolesListForDeliverable,
} from "../../../../../planner/milestone/utils/budget.utils";
import { getFormattedExpenseDataForProject } from "../single-project/single-project-financials.utils";
import { getTime } from "../../../../../../services/projects-services";
import {
  getProjectBudgetForGroupRow,
  shouldShowHoursForProjectGroup,
} from "./grouped-project-financials.utils";

const GroupedProjectFinancials = () => {
  const [expanded, setExpanded] = useState("");
  const { id } = useParams();
  const { linkedProjects } = useLinkedProjects(id);
  const projects = linkedProjects ?? [];
  const showGroupHours = shouldShowHoursForProjectGroup(projects);

  const { data: users, isLoading: isUsersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => getTime(),
  });

  if (isUsersLoading) {
    return <Loader absolute />;
  }

  let totalGroupBudgetCost = 0;
  let totalGroupBudgetHours = 0;
  let totalGroupExpenseCost = 0;
  let totalGroupExpenseHours = 0;

  projects.forEach((project) => {
    const deliverables = project?.deliverables || [];
    const resources = project?.resources || [];
    const { hours: projBudgetHours, value: projBudgetCost } =
      getProjectBudgetForGroupRow(
        deliverables,
        resources,
        project?.plannerType,
        project?.plannerServiceValue
      );
    totalGroupBudgetCost += projBudgetCost;
    totalGroupBudgetHours += projBudgetHours;

    const expenses = getFormattedExpenseDataForProject(
      users,
      project?.id,
      resources
    );
    const projExpense = expenses.reduce(
      (acc, entry) => ({
        hours: acc.hours + (entry?.hours || 0),
        cost: acc.cost + (entry?.cost || 0),
      }),
      { hours: 0, cost: 0 }
    );
    totalGroupExpenseCost += projExpense.cost;
    totalGroupExpenseHours += projExpense.hours;
  });

  const overallPercentage =
    totalGroupBudgetCost > 0
      ? ((totalGroupExpenseCost / totalGroupBudgetCost) * 100).toFixed(2)
      : 0;

  const groupEndLabel = showGroupHours
    ? `${formatNumber(totalGroupExpenseHours)} / ${formatNumber(
        totalGroupBudgetHours
      )} hrs (${overallPercentage}%)`
    : `(${overallPercentage}%)`;

  return (
    <ProjectFinancialsStyled>
      <Typography variant="h3" sx={{ color: "#301D35", marginBottom: "20px" }}>
        Project Group Financials
      </Typography>

      <div className="prog-wrap">
        <ProgressBar
          value={overallPercentage}
          startLabel={"Project Group Totals (act. / est.)"}
          middleLabel={`$ ${formatNumber(
            totalGroupExpenseCost
          )} / $ ${formatNumber(totalGroupBudgetCost)}`}
          endLabel={groupEndLabel}
        />
      </div>

      <div className="accordian-padding">
        {projects.map((project) => {
          const deliverables = project?.deliverables || [];
          const resources = project?.resources || [];
          const plannerType = project?.plannerType;

          const { hours: projBudgetHours, value: projBudgetCost } =
            getProjectBudgetForGroupRow(
              deliverables,
              resources,
              plannerType,
              project?.plannerServiceValue
            );

          const expenses = getFormattedExpenseDataForProject(
            users,
            project?.id,
            resources
          );
          const projExpense = expenses.reduce(
            (acc, entry) => ({
              hours: acc.hours + (entry?.hours || 0),
              cost: acc.cost + (entry?.cost || 0),
            }),
            { hours: 0, cost: 0 }
          );
          const projectPercentage =
            projBudgetCost > 0
              ? ((projExpense.cost / projBudgetCost) * 100).toFixed(2)
              : 0;

          const projectEndLabel = showGroupHours
            ? `${formatNumber(projExpense.hours)} / ${formatNumber(
                projBudgetHours
              )} hrs (${projectPercentage}%)`
            : `(${projectPercentage}%)`;

          const otherActuals = expenses.filter((entry) => {
            const deliverableMatch = deliverables?.find(
              (deliv) => String(deliv?.name) === String(entry?.deliverable)
            );
            if (!deliverableMatch) return true;
            const budgetedRoles = getRolesListForDeliverable(
              deliverableMatch,
              resources
            );
            const hasBudgetedRole = budgetedRoles.some(
              (role) =>
                String(role.role).toLowerCase() ===
                String(entry?.role).toLowerCase()
            );
            return !hasBudgetedRole;
          });

          const otherActualsSummary = otherActuals.reduce(
            (totals, entry) => {
              const hours = entry?.hours || 0;
              const cost = entry?.cost || 0;
              return {
                hours: totals.hours + hours,
                cost: totals.cost + cost,
              };
            },
            { hours: 0, cost: 0 }
          );

          const showOtherActualsSection =
            (!plannerType || plannerType === "Time + Materials") &&
            (otherActualsSummary.hours > 0 || otherActualsSummary.cost > 0);

          return (
            <Accordian
              key={project?.id}
              name={project?.projectName}
              handleChange={setExpanded}
              expanded={expanded}
              arrowPosition="right"
              heading={
                <div style={{ width: "100%" }}>
                  <ProgressBar
                    value={projectPercentage}
                    startLabel={`${project?.projectName} (act. / est.)`}
                    middleLabel={`$ ${formatNumber(
                      projExpense.cost
                    )} / $ ${formatNumber(projBudgetCost)}`}
                    endLabel={projectEndLabel}
                  />
                </div>
              }
              body={
                <div className="accordian-body">
                  {deliverables?.map((deliverable) => {
                    const deliverableExpenses = expenses.filter(
                      (entry) =>
                        String(entry?.deliverable) === String(deliverable?.name)
                    );
                    const {
                      hours: delivExpenseHours,
                      cost: delivExpenseCost,
                    } = deliverableExpenses.reduce(
                      (totals, entry) => ({
                        hours: totals.hours + (entry?.hours || 0),
                        cost: totals.cost + (entry?.cost || 0),
                      }),
                      { hours: 0, cost: 0 }
                    );

                    if (plannerType === "Fixed Fee Project") {
                      return (
                        <DeliverableProgressWithLabel
                          key={deliverable?.id}
                          progressContent={
                            <ProgressBar
                              value={100}
                              startLabel={deliverable?.name}
                              middleLabel={`$ ${formatNumber(
                                delivExpenseCost
                              )}`}
                              endLabel={
                                showGroupHours
                                  ? `${formatNumber(delivExpenseHours)} hrs`
                                  : ""
                              }
                            />
                          }
                          className={"deliverable"}
                        />
                      );
                    }

                    if (plannerType === "Fixed Fee Deliverable") {
                      const delivBudgetCost = Number(deliverable?.budget) || 0;
                      const deliverablePercentage =
                        delivBudgetCost > 0
                          ? (
                              (delivExpenseCost / delivBudgetCost) *
                              100
                            ).toFixed(2)
                          : 0;
                      return (
                        <DeliverableProgressWithLabel
                          key={deliverable?.id}
                          progressContent={
                            <ProgressBar
                              value={deliverablePercentage}
                              startLabel={`${deliverable?.name} (act. / est.)`}
                              middleLabel={`$ ${formatNumber(
                                delivExpenseCost
                              )} / $ ${formatNumber(delivBudgetCost)}`}
                              endLabel={
                                showGroupHours
                                  ? `${formatNumber(
                                      delivExpenseHours
                                    )} hrs (act.) (${deliverablePercentage}%)`
                                  : `(${deliverablePercentage}%)`
                              }
                            />
                          }
                          className={"deliverable"}
                        />
                      );
                    }

                    const { hours: delivBudgetHours, value: delivBudgetCost } =
                      calculateBudgetForDeliverable(deliverable, resources);
                    const deliverablePercentage =
                      delivBudgetCost > 0
                        ? (
                            (delivExpenseCost / delivBudgetCost) *
                            100
                          ).toFixed(2)
                        : 0;
                    return (
                      <DeliverableProgressWithLabel
                        key={deliverable?.id}
                        progressContent={
                          <ProgressBar
                            value={deliverablePercentage}
                            startLabel={deliverable?.name}
                            middleLabel={`$ ${formatNumber(
                              delivExpenseCost
                            )} / $ ${formatNumber(delivBudgetCost)}`}
                            endLabel={
                              showGroupHours
                                ? `${formatNumber(
                                    delivExpenseHours
                                  )} / ${formatNumber(
                                    delivBudgetHours
                                  )} hrs (${deliverablePercentage}%)`
                                : `(${deliverablePercentage}%)`
                            }
                          />
                        }
                        className={"deliverable"}
                      />
                    );
                  })}

                  {showOtherActualsSection && (
                    <div className={"deliverable"}>
                      <ProgressBar
                        value={100}
                        startLabel="Other Actuals (act)"
                        middleLabel={`$ ${formatNumber(
                          otherActualsSummary.cost
                        )}`}
                        endLabel={
                          showGroupHours
                            ? `${formatNumber(otherActualsSummary.hours)} hrs`
                            : ""
                        }
                        description="Time tracked against this project/deliverable without a budgeted role."
                      />
                    </div>
                  )}
                </div>
              }
            />
          );
        })}
      </div>
    </ProjectFinancialsStyled>
  );
};

export default GroupedProjectFinancials;
